import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LogOut, Users2, PlusCircle, Copy, Calculator, ExternalLink, IdCard, QrCode, Share2 } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const DIVISIONS = [
  { value: "academy", label: "Academy" },
  { value: "study_abroad", label: "Study Abroad" },
  { value: "mbbs_abroad", label: "MBBS Abroad" },
  { value: "wealth", label: "Wealth" },
  { value: "insurance", label: "Insurance" },
  { value: "franchise", label: "Franchise" },
];

const EMPTY_FORM = {
  name: "", phone: "", email: "", division: "academy",
  city: "", interest: "", notes: "",
  admission_type: "", admission_program: "", admission_mode: "",
  admission_duration: "", admission_fee: "",
};

export default function FranchisePortal() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("leads");
  const [form, setForm] = useState(EMPTY_FORM);
  const [showEnrolment, setShowEnrolment] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/portal/franchise").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Lead name is required");
    if (!form.phone.trim() && !form.email.trim()) {
      return toast.error("Enter at least a phone or an email");
    }
    setSubmitting(true);
    try {
      const body = { ...form };
      // Strip empty strings so backend validators don't choke on Optional fields.
      Object.keys(body).forEach(k => {
        if (body[k] === "" || body[k] === null) delete body[k];
      });
      if (body.admission_fee !== undefined) {
        body.admission_fee = Number(body.admission_fee);
        if (Number.isNaN(body.admission_fee)) delete body.admission_fee;
      }
      await api.post("/portal/leads", body);
      toast.success("Lead added and assigned for follow-up");
      setForm(EMPTY_FORM);
      setShowEnrolment(false);
      await load();
      setActiveTab("leads");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save lead");
    } finally {
      setSubmitting(false);
    }
  };

  const partner = data?.partner;
  const commission = data?.commissions;

  return (
    <div className="min-h-screen bg-[var(--miro-bg)]">
      <header className="bg-white border-b border-[var(--miro-border)]">
        <div className="max-w-5xl mx-auto flex items-center gap-3 px-6 py-4">
          <div className="w-10 h-10 rounded-lg bg-white border border-[var(--miro-border)] p-1">
            <img src="/miro-logo.png" alt="MIRO" className="w-full h-full object-contain"/>
          </div>
          <div>
            <div className="font-display font-black text-lg text-[var(--miro-navy)] tracking-tight">MIRO Franchise Portal</div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--miro-text-muted)]">Your leads. Your revenue. Your growth.</div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-[var(--miro-navy)]">{user?.name}</div>
              <div className="text-[11px] text-[var(--miro-text-muted)]">Franchise partner</div>
            </div>
            <Button data-testid="portal-logout" variant="outline" size="sm" onClick={logout}>
              <LogOut size={14} className="mr-1"/> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="rounded-2xl p-6 text-white relative overflow-hidden miro-grain mb-6" data-testid="portal-hero"
             style={{ background: "var(--miro-navy)" }}>
          <div className="absolute inset-0" style={{ background: "radial-gradient(500px at 90% 20%, rgba(14,165,255,0.35), transparent 60%)" }}/>
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <div className="miro-overline text-[var(--miro-blue)]">Commission overview</div>
              <div className="font-display font-black text-4xl mt-2">
                ₹{(commission?.total || 0).toLocaleString("en-IN")}
              </div>
              <div className="text-sm text-white/70 mt-2">
                Earned across {commission?.rows?.length || 0} won opportunities.
                {partner ? ` Commission rate: ${partner.commission_rate}%.` : ""}
              </div>
            </div>
            {partner && (
              <div className="text-sm text-white/80">
                <div className="miro-overline text-[var(--miro-blue)]">Your franchise</div>
                <div className="font-display font-bold text-lg mt-2">{partner.name}</div>
                <div className="mt-1">{partner.city}</div>
                <div className="mt-1 text-white/60 text-xs">{partner.contact_email}</div>
              </div>
            )}
          </div>
        </div>

        {/* Training tools strip — referral link + Earnings Calculator */}
        <ToolsStrip partnerCode={partner?.partner_code || partner?.code}
                    userEmail={user?.email}
                    partner={partner}
                    isDemoTraining={user?.email?.startsWith("franchise@miro.one")
                                    || user?.email?.startsWith("partner@miro.one")}/>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList data-testid="portal-tabs" className="bg-white border border-[var(--miro-border)]">
            <TabsTrigger value="leads" data-testid="portal-tab-leads">Your leads</TabsTrigger>
            <TabsTrigger value="commissions" data-testid="portal-tab-commissions">Commissions</TabsTrigger>
            <TabsTrigger value="add-lead" data-testid="portal-tab-add-lead">
              <PlusCircle size={13} className="mr-1"/> Add Lead / Enrolment
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="mt-4">
            <div className="space-y-2">
              {(data?.leads || []).length === 0 && (
                <div className="miro-card p-10 text-center">
                  <Users2 size={30} className="mx-auto text-[var(--miro-text-muted)]"/>
                  <div className="text-sm text-[var(--miro-text-muted)] mt-3">
                    No leads assigned yet. Share your public form URL to start referring.
                  </div>
                </div>
              )}
              {(data?.leads || []).map(l => (
                <div key={l.id} data-testid={`portal-lead-${l.id}`} className="miro-card p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#0EA5FF18", color: "#0284C7" }}>
                    <Users2 size={17}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[var(--miro-navy)]">{l.name}</div>
                    <div className="text-xs text-[var(--miro-text-muted)]">{l.email || l.phone} · {l.division}</div>
                  </div>
                  <Badge className="border-0 font-semibold capitalize" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>{l.status}</Badge>
                  <div className="text-[11px] text-[var(--miro-text-muted)] hidden md:block">
                    {formatDistanceToNow(new Date(l.created_at), { addSuffix: true })}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="commissions" className="mt-4">
            <div className="miro-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[var(--miro-bg)] text-[var(--miro-text-secondary)]">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-semibold">Opportunity</th>
                    <th className="px-4 py-3 font-semibold text-right">Deal value</th>
                    <th className="px-4 py-3 font-semibold text-right">Your commission</th>
                  </tr>
                </thead>
                <tbody>
                  {(commission?.rows || []).length === 0 && (
                    <tr><td colSpan={3} className="px-4 py-12 text-center text-[var(--miro-text-muted)]">No won deals yet.</td></tr>
                  )}
                  {(commission?.rows || []).map(r => (
                    <tr key={r.opportunity_id} data-testid={`portal-comm-row-${r.opportunity_id}`} className="border-t border-[var(--miro-border)]">
                      <td className="px-4 py-3 font-semibold text-[var(--miro-navy)]">{r.title}</td>
                      <td className="px-4 py-3 text-right font-mono">₹{(r.value || 0).toLocaleString("en-IN")}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-[var(--miro-green-600)]">₹{(r.commission || 0).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="add-lead" className="mt-4">
            <form onSubmit={submit} className="miro-card p-6 space-y-5" data-testid="portal-add-lead-form">
              <div>
                <div className="miro-overline text-[var(--miro-text-muted)]">New lead</div>
                <div className="font-display font-bold text-lg text-[var(--miro-navy)] mt-1">
                  Refer someone yourself
                </div>
                <div className="text-xs text-[var(--miro-text-muted)] mt-1">
                  Every lead you enter here is tagged to you and auto-assigned to the right counsellor.
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pl-name">Lead name *</Label>
                  <Input id="pl-name" data-testid="pl-name" value={form.name}
                         onChange={e => setField("name", e.target.value)} required/>
                </div>
                <div>
                  <Label htmlFor="pl-division">Interested in *</Label>
                  <select id="pl-division" data-testid="pl-division"
                          className="w-full h-9 rounded-md border border-[var(--miro-border)] bg-white px-3 text-sm"
                          value={form.division}
                          onChange={e => setField("division", e.target.value)}>
                    {DIVISIONS.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="pl-phone">Phone</Label>
                  <Input id="pl-phone" data-testid="pl-phone" value={form.phone}
                         onChange={e => setField("phone", e.target.value)}/>
                </div>
                <div>
                  <Label htmlFor="pl-email">Email</Label>
                  <Input id="pl-email" data-testid="pl-email" type="email" value={form.email}
                         onChange={e => setField("email", e.target.value)}/>
                </div>
                <div>
                  <Label htmlFor="pl-city">City</Label>
                  <Input id="pl-city" data-testid="pl-city" value={form.city}
                         onChange={e => setField("city", e.target.value)}/>
                </div>
                <div>
                  <Label htmlFor="pl-interest">Program of interest</Label>
                  <Input id="pl-interest" data-testid="pl-interest" value={form.interest}
                         onChange={e => setField("interest", e.target.value)}
                         placeholder="e.g. MBA, MBBS Ukraine, PMP"/>
                </div>
              </div>

              <div>
                <Label htmlFor="pl-notes">Notes</Label>
                <Textarea id="pl-notes" data-testid="pl-notes" rows={3} value={form.notes}
                          onChange={e => setField("notes", e.target.value)}
                          placeholder="What did you discuss? Budget, timing, concerns…"/>
              </div>

              <div className="border-t border-[var(--miro-border)] pt-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" data-testid="pl-toggle-enrolment"
                         checked={showEnrolment}
                         onChange={e => setShowEnrolment(e.target.checked)}/>
                  <span className="font-semibold text-[var(--miro-navy)]">
                    Already enrolled offline? Record enrolment details
                  </span>
                </label>
                {showEnrolment && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4"
                       data-testid="pl-enrolment-section">
                    <div>
                      <Label htmlFor="pl-adm-type">Program type</Label>
                      <Input id="pl-adm-type" data-testid="pl-adm-type"
                             value={form.admission_type}
                             onChange={e => setField("admission_type", e.target.value)}
                             placeholder="MBA, MBBS-India, BTech…"/>
                    </div>
                    <div>
                      <Label htmlFor="pl-adm-program">Institution / program</Label>
                      <Input id="pl-adm-program" data-testid="pl-adm-program"
                             value={form.admission_program}
                             onChange={e => setField("admission_program", e.target.value)}/>
                    </div>
                    <div>
                      <Label htmlFor="pl-adm-mode">Mode</Label>
                      <select id="pl-adm-mode" data-testid="pl-adm-mode"
                              className="w-full h-9 rounded-md border border-[var(--miro-border)] bg-white px-3 text-sm"
                              value={form.admission_mode}
                              onChange={e => setField("admission_mode", e.target.value)}>
                        <option value="">Select mode</option>
                        <option value="online">Online</option>
                        <option value="offline">Offline</option>
                        <option value="hybrid">Hybrid</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="pl-adm-duration">Duration</Label>
                      <Input id="pl-adm-duration" data-testid="pl-adm-duration"
                             value={form.admission_duration}
                             onChange={e => setField("admission_duration", e.target.value)}
                             placeholder="e.g. 2 years"/>
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="pl-adm-fee">Fee collected / expected (₹)</Label>
                      <Input id="pl-adm-fee" data-testid="pl-adm-fee" type="number" min="0" step="1"
                             value={form.admission_fee}
                             onChange={e => setField("admission_fee", e.target.value)}/>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 border-t border-[var(--miro-border)] pt-4">
                <Button type="button" variant="outline" data-testid="pl-reset"
                        onClick={() => { setForm(EMPTY_FORM); setShowEnrolment(false); }}>
                  Reset
                </Button>
                <Button type="submit" data-testid="pl-submit" disabled={submitting}>
                  {submitting ? "Saving…" : "Add lead"}
                </Button>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}



function ToolsStrip({ partnerCode, userEmail, isDemoTraining, partner }) {
  const referralUrl = partnerCode
    ? `${window.location.origin}/lead?ref=${encodeURIComponent(partnerCode)}`
    : null;
  const qrUrl = referralUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(referralUrl)}`
    : null;

  const copyReferral = () => {
    if (!referralUrl) return;
    navigator.clipboard.writeText(referralUrl)
      .then(() => toast.success("Lead form link copied"))
      .catch(() => toast.error("Could not copy"));
  };

  const shareReferral = async () => {
    if (!referralUrl) return;
    if (navigator.share) {
      try { await navigator.share({ title: "MIRO Consultancy — Share your details", url: referralUrl }); }
      catch { /* user cancelled */ }
    } else {
      copyReferral();
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Digital ID card */}
      <div className="miro-card p-5" data-testid="portal-digital-id-card">
        <div className="flex items-start justify-between">
          <div>
            <div className="miro-overline text-[var(--miro-blue)]">Your Digital ID</div>
            <div className="font-display font-bold text-lg mt-1 text-[var(--miro-navy)]">
              {partner?.name || "Franchise Partner"}
            </div>
            <div className="text-xs text-[var(--miro-text-muted)] mt-0.5">
              {partner?.city} {partner?.city && "·"} {userEmail}
            </div>
          </div>
          <div className="w-10 h-10 rounded-lg bg-[var(--miro-navy)] flex items-center justify-center">
            <IdCard className="text-white" size={20}/>
          </div>
        </div>
        {partnerCode ? (
          <div className="mt-4 p-3 rounded-lg bg-gradient-to-br from-slate-900 to-slate-700 text-white">
            <div className="text-[10px] uppercase tracking-widest text-white/60">Franchise ID</div>
            <div className="font-mono font-bold text-lg mt-0.5" data-testid="portal-partner-code">
              {partnerCode}
            </div>
            <div className="text-[10px] text-white/50 mt-1">
              Verifiable at /verify/{partnerCode}
            </div>
          </div>
        ) : (
          <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
            Your Franchise ID hasn't been issued yet. It appears here as soon as
            super admin approves your application.
          </div>
        )}
        <div className="mt-3 flex gap-2">
          <a href="/my-id"
             data-testid="portal-open-digital-id"
             className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded bg-[var(--miro-navy)] text-white hover:opacity-90">
            <IdCard size={14}/> View full ID card
          </a>
        </div>
      </div>

      {/* Lead Form panel */}
      <div className="miro-card p-5" data-testid="portal-lead-form-card">
        <div className="flex items-start justify-between">
          <div>
            <div className="miro-overline text-[var(--miro-blue)]">Your Lead Form</div>
            <div className="font-display font-bold text-lg mt-1 text-[var(--miro-navy)]">
              Share &amp; capture leads
            </div>
            <div className="text-xs text-[var(--miro-text-muted)] mt-0.5">
              Every lead submitted via this link is tagged to your Franchise ID.
            </div>
          </div>
          <div className="w-10 h-10 rounded-lg bg-[var(--miro-blue)] flex items-center justify-center">
            <QrCode className="text-white" size={20}/>
          </div>
        </div>
        {referralUrl ? (
          <>
            <div className="mt-4 flex gap-3 items-start">
              {qrUrl && (
                <img src={qrUrl} alt="Lead form QR code"
                     className="w-24 h-24 border border-[var(--miro-border)] rounded-lg bg-white p-1 shrink-0"
                     data-testid="portal-lead-qr"/>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[var(--miro-text-muted)] mb-1">Your unique link</div>
                <div className="text-xs font-mono break-all bg-[var(--miro-bg)] p-2 rounded border border-[var(--miro-border)]"
                     data-testid="portal-referral-link">{referralUrl}</div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={copyReferral}
                      data-testid="portal-referral-copy">
                <Copy size={13} className="mr-1"/> Copy link
              </Button>
              <Button size="sm" variant="outline" onClick={shareReferral}
                      data-testid="portal-referral-share">
                <Share2 size={13} className="mr-1"/> Share
              </Button>
              <a href={referralUrl} target="_blank" rel="noopener noreferrer"
                 data-testid="portal-referral-open"
                 className="inline-flex items-center gap-1 text-sm px-3 py-1 border rounded hover:bg-slate-50">
                <ExternalLink size={13}/> Preview
              </a>
            </div>
          </>
        ) : (
          <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
            Your unique lead form will appear here once your Franchise ID is issued.
          </div>
        )}
      </div>

      {/* Earnings calculator quick link */}
      <div className="miro-card p-4 md:col-span-2" data-testid="portal-calculator-strip">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="miro-overline text-[var(--miro-blue)]">Earnings calculator</div>
            <div className="text-xs text-[var(--miro-text-muted)] mt-1">
              Estimate what you can earn from each product, before you refer a lead.
            </div>
          </div>
          <a href="/finance/commissions"
             data-testid="portal-open-calculator"
             className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded bg-[var(--miro-navy)] text-white hover:opacity-90 shrink-0">
            <Calculator size={14}/> Open Calculator
          </a>
        </div>
      </div>

      {isDemoTraining && (
        <div className="md:col-span-2 px-3 py-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900">
          <b>Training account.</b> Demo login for onboarding new partners; data
          added here is not real business data.
        </div>
      )}
    </div>
  );
}

